/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';
import {
  rubricFamilyIdOf,
  sortRubricFamilies,
} from '@linc-technologies/linced-common/utils/rubrics';
import {
  rollbackRecord,
  saveOrDiscard,
} from '@linc-technologies/linced-common/utils/record-rollback';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import { apiErrorDetail } from 'linced-admin/utils/api-error-detail';

export default class IndexController extends Controller {
  @action
  setNewRubricSubject(value) {
    this.newRubricSubject = value;
  }

  queryParams = ['jurisdiction'];

  // Services
  @service
  store;

  @service
  toaster;

  @service
  router;

  @tracked jurisdiction = 1;
  Jurisdiction = Jurisdiction;

  @computed
  get jurisdictionOptions() {
    return Jurisdiction.values;
  }

  get selectedJurisdiction() {
    let value = this.jurisdiction;
    return Jurisdiction.values.find((j) => j.value === value) || null;
  }

  @tracked
  selectedMilestoneSetFilter = null;
  @tracked selectedMilestoneId = null;
  @tracked selectedSubjectId = null;
  @tracked
  showNewRubricDialog = false;
  @tracked newRubricSubject = null;
  @tracked
  showDeleteDialog = false;
  @tracked
  rubricToDelete = null;

  @tracked selectedFamilyId = null;
  @tracked newRubricFamily = null;
  @tracked showFamiliesDialog = false;
  @tracked newFamilyName = '';
  @tracked newFamilyDescription = '';

  // Every loaded rubric family; a live list, so new and deleted families show
  get allFamilies() {
    return this.store.peekAll('reports/rubric-family');
  }

  // The selected jurisdiction's rubric families, default first
  @computed(
    'allFamilies.@each.{name,isDefault,jurisdiction,isDeleted}',
    'jurisdiction',
  )
  get families() {
    return sortRubricFamilies(
      this.allFamilies.filter(
        (f) => !f.isDeleted && !f.isNew && f.jurisdiction === this.jurisdiction,
      ),
    );
  }

  @computed('families.@each.isDefault')
  get defaultFamily() {
    return this.families.find((f) => f.isDefault) || null;
  }

  DEFAULT_COLUMNS = Object.freeze([
    {
      column: 1,
      label: 'Emerging',
      colour: '#e91e90',
      colourClass: 'emerging',
    },
    {
      column: 2,
      label: 'Developing',
      colour: '#ff9800',
      colourClass: 'developing',
    },
    {
      column: 3,
      label: 'Consolidating',
      colour: '#4dd0b8',
      colourClass: 'consolidating',
    },
    {
      column: 4,
      label: 'Proficient',
      colour: '#00897b',
      colourClass: 'proficient',
    },
    {
      column: 5,
      label: 'Exceeding',
      colour: '#ba68c8',
      colourClass: 'exceeding',
    },
  ]);

  get newRubricColumns() {
    if (!this.newRubricSubject) {
      return [];
    }
    return this.DEFAULT_COLUMNS;
  }

  get canCreateRubric() {
    return !!this.newRubricSubject;
  }

  @computed('model.templateSets.[]')
  get sortedTemplateSets() {
    return (this.get('model.templateSets') || [])
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed('selectedMilestoneSetFilter.{milestones.[],milestoneSetTemplateId}')
  get filterMilestones() {
    let set = this.selectedMilestoneSetFilter;
    if (!set) {
      return [];
    }
    let msTemplateId = set.belongsTo('milestoneSetTemplateId').id();
    if (msTemplateId) {
      return this.store
        .peekAll('milestones/milestone')
        .filter(
          (m) => m.belongsTo('milestoneSetTemplateId').id() === msTemplateId,
        )
        .sort((a, b) =>
          a.sequence > b.sequence ? 1 : a.sequence < b.sequence ? -1 : 0,
        );
    }
    return (set.get('milestones') || [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence);
  }

  @computed('model.subjects.[]')
  get sortedSubjects() {
    return (this.get('model.subjects') || [])
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed(
    'model.rubrics.@each.{name,templateSubjectId,templateMilestoneIds,next,jurisdiction,familyId}',
    'selectedMilestoneId',
    'selectedSubjectId',
    'selectedFamilyId',
    'families.[]',
    'defaultFamily.id',
    'jurisdiction',
  )
  get filteredRubrics() {
    let jurisdiction = this.jurisdiction;
    let rubrics = (this.get('model.rubrics') || []).filter(
      (r) => !r.get('next') && r.get('jurisdiction') === jurisdiction,
    );

    // Rubrics saved before families existed belong to the default family
    let defaultFamilyId = this.defaultFamily?.id ?? null;
    let familyIdOf = (r) => rubricFamilyIdOf(r, defaultFamilyId);

    // Flag rubrics that share a family, subject and milestone with another
    // (no milestones means every milestone); report sections can't tell
    // which one to use
    let overlapping = new Set();
    rubrics.forEach((a, i) => {
      let am = [...(a.templateMilestoneIds || [])];
      rubrics.slice(i + 1).forEach((b) => {
        if (
          familyIdOf(a) !== familyIdOf(b) ||
          a.templateSubjectId !== b.templateSubjectId
        ) {
          return;
        }
        let bm = [...(b.templateMilestoneIds || [])];
        if (!am.length || !bm.length || am.some((id) => bm.includes(id))) {
          overlapping.add(a.id);
          overlapping.add(b.id);
        }
      });
    });

    if (this.selectedFamilyId) {
      rubrics = rubrics.filter((r) => familyIdOf(r) === this.selectedFamilyId);
    }

    let milestoneId = this.selectedMilestoneId;
    let subjectId = this.selectedSubjectId;

    if (milestoneId) {
      rubrics = rubrics.filter((r) => {
        let ids = r.get('templateMilestoneIds') || [];
        return ids.includes(milestoneId);
      });
    }

    if (subjectId) {
      rubrics = rubrics.filter((r) => r.get('templateSubjectId') === subjectId);
    }

    let allElements = this.store.peekAll('reports/rubric-element');
    let allCriteria = this.store.peekAll('reports/rubric-criteria');

    return rubrics
      .map((r) => {
        let rubricId = r.get('id');

        let subject = r.get('templateSubjectId')
          ? this.store.peekRecord('goals/subject', r.get('templateSubjectId'))
          : null;

        let milestoneIds = r.get('templateMilestoneIds')?.toArray() ?? [];
        let milestones = milestoneIds
          .map((id) => this.store.peekRecord('milestones/milestone', id))
          .filter(Boolean)
          .sort(byKeys('sequence'));

        let elements = allElements.filter(
          (e) => e.belongsTo('rubricId').id() === rubricId,
        );
        let criteriaCount = allCriteria.filter(
          (c) => c.belongsTo('rubricId').id() === rubricId,
        ).length;

        let familyId = familyIdOf(r);
        r.set('_subject', subject);
        r.set('_milestones', milestones);
        r.set('_elementCount', elements.length);
        r.set('_criteriaCount', criteriaCount);
        r.set(
          '_family',
          familyId
            ? this.store.peekRecord('reports/rubric-family', familyId)
            : null,
        );
        r.set('_overlaps', overlapping.has(rubricId));

        return r;
      })
      .sort(naturalSort('name'));
  }

  @action
  toggleMilestone(milestone) {
    let id = milestone.get('id');
    this.selectedMilestoneId = this.selectedMilestoneId === id ? null : id;
  }

  @action
  toggleSubject(subject) {
    let id = subject.get('id');
    this.selectedSubjectId = this.selectedSubjectId === id ? null : id;
  }

  @action
  selectMilestoneSetFilter(set) {
    this.selectedMilestoneSetFilter = set;
    this.selectedMilestoneId = null;
  }

  @action
  clearMilestoneFilter() {
    this.selectedMilestoneId = null;
  }

  @action
  clearSubjectFilter() {
    this.selectedSubjectId = null;
  }

  @action
  toggleFamily(family) {
    let id = family.id;
    this.selectedFamilyId = this.selectedFamilyId === id ? null : id;
  }

  @action
  clearFamilyFilter() {
    this.selectedFamilyId = null;
  }

  @action
  openFamiliesDialog() {
    this.newFamilyName = '';
    this.newFamilyDescription = '';
    this.showFamiliesDialog = true;
  }

  @action
  closeFamiliesDialog() {
    this.showFamiliesDialog = false;
  }

  @action
  setNewFamilyName(value) {
    this.newFamilyName = value;
  }

  @action
  setNewFamilyDescription(value) {
    this.newFamilyDescription = value;
  }

  @action
  setNewRubricFamily(family) {
    this.newRubricFamily = family;
  }

  @action
  async addFamily() {
    let name = (this.newFamilyName || '').trim();
    if (!name) return;
    let family = this.store.createRecord('reports/rubric-family', {
      name,
      description: (this.newFamilyDescription || '').trim(),
      jurisdiction: this.jurisdiction,
      // The first family in a jurisdiction becomes its default
      isDefault: !this.defaultFamily,
    });
    try {
      await saveOrDiscard(family);
      this.newFamilyName = '';
      this.newFamilyDescription = '';
    } catch (e) {
      this.toaster.error(apiErrorDetail(e, 'Failed to create family.'));
    }
  }

  // Saves a family's name or description edits when the field loses focus
  @action
  async saveFamily(family) {
    if (!family.hasDirtyAttributes) return;
    if (!(family.name || '').trim()) {
      rollbackRecord(family);
      return;
    }
    try {
      await family.save();
    } catch (e) {
      rollbackRecord(family);
      this.toaster.error(apiErrorDetail(e, 'Failed to save family.'));
    }
  }

  @action
  async makeDefaultFamily(family) {
    family.isDefault = true;
    try {
      await family.save();
      // The API unsets the previous default; reload to reflect it
      await this.store.query('reports/rubric-family', {
        jurisdiction: this.jurisdiction,
      });
    } catch (e) {
      rollbackRecord(family);
      this.toaster.error(apiErrorDetail(e, 'Failed to change the default.'));
    }
  }

  @action
  async deleteFamily(family) {
    let { id, name } = family;
    try {
      await family.destroyRecord();
      if (this.selectedFamilyId === id) {
        this.selectedFamilyId = null;
      }
      this.toaster.success(`Family "${name}" deleted.`);
    } catch (e) {
      rollbackRecord(family);
      this.toaster.error(
        apiErrorDetail(e, `Failed to delete family "${name}".`),
      );
    }
  }

  @action
  selectJurisdiction(option) {
    // Families are per jurisdiction, so the family filter can't carry over
    this.jurisdiction = option ? option.value : 1;
    this.selectedFamilyId = null;
  }

  @action
  openNewRubricDialog() {
    this.newRubricSubject = null;
    this.newRubricFamily = this.defaultFamily;
    this.showNewRubricDialog = true;
  }

  @action
  closeNewRubricDialog() {
    this.showNewRubricDialog = false;
  }

  @action
  async createRubric() {
    let subject = this.newRubricSubject;
    let columns = this.DEFAULT_COLUMNS.map((c) => ({
      column: c.column,
      label: c.label,
    }));
    let rubric = this.store.createRecord('reports/rubric', {
      name: 'New Rubric',
      description: '',
      templateSubjectId: subject.get('id'),
      templateMilestoneIds: [],
      columns: columns,
      isGlobal: true,
      isActive: true,
      jurisdiction: this.jurisdiction,
      familyId: this.newRubricFamily?.id ?? '',
    });

    try {
      await rubric.save();
      this.showNewRubricDialog = false;
      this.router.transitionTo('rubrics.rubric', rubric);
    } catch (e) {
      rubric.deleteRecord();
      this.toaster.error(apiErrorDetail(e, 'Failed to create rubric.'));
    }
  }

  @action
  editRubric(rubric) {
    this.router.transitionTo('rubrics.rubric', rubric);
  }

  @action
  deleteRubric(rubric) {
    this.rubricToDelete = rubric;
    this.showDeleteDialog = true;
  }

  @action
  closeDeleteDialog() {
    this.showDeleteDialog = false;
    this.rubricToDelete = null;
  }

  @action
  async confirmDeleteRubric() {
    let rubric = this.rubricToDelete;
    let rubricName = rubric.get('name');

    this.showDeleteDialog = false;
    this.rubricToDelete = null;

    try {
      await rubric.destroyRecord();
      this.toaster.success(`Rubric "${rubricName}" deleted.`);
    } catch {
      this.toaster.error(`Failed to delete rubric "${rubricName}".`);
    }
  }
}
